import { motion } from "framer-motion";
import { Check } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { DEFAULT_PRICING, localitySlug, PHONE_DISPLAY, PHONE_TEL, formatLei } from "../lib/api";

const INCLUDED = [
  "Foraj de la 10 la 150 de metri, în funcție de unde se găsește pânza de apă",
  "Tubulatură PVC albastră cu filet, certificată pentru apă potabilă (Ø125, Ø140 sau Ø160)",
  "Filtru și pietriș mărgăritar montate la baza puțului",
  "Denisipare și pompaj de limpezire, până iese apa curată",
  "Test de debit la predarea puțului",
  "Estimare de preț calculată online, înainte să ne suni",
];

export default function LocalityContentSection({ locality }) {
  const name = locality.name;
  const transport =
    locality.transport > 0
      ? `${formatLei(locality.transport)} lei`
      : "inclusă în preț";

  const idx = DEFAULT_PRICING.localities.findIndex((l) => l.name === name);
  const start = Math.max(0, Math.min(idx - 2, DEFAULT_PRICING.localities.length - 4));
  const around = DEFAULT_PRICING.localities.filter((_, i) => i !== idx).slice(start, start + 4);

  const faqs = [
    {
      q: `Cât costă un puț forat în ${name}?`,
      a: `Poți afla prețul orientativ imediat, din calculatorul de pe această pagină — el include deja forajul, țeava PVC albastră cu filet, filtrele și deplasarea în ${name} (${transport}). Prețul ferm îl stabilim după vizita la teren, când vedem terenul și locul de acces.`,
    },
    {
      q: `Cât durează un foraj în ${name}?`,
      a: "Pentru adâncimile uzuale, de regulă o singură zi de lucru. Durata exactă depinde de tipul de teren și de adâncimea la care se găsește apa.",
    },
    {
      q: `Lucrați și în zona mea din ${name}?`,
      a: `Da — acoperim ${name} și toate localitățile din județ: de la Otopeni, Voluntari și Tunari până la Snagov, Periș și Copăceni. Sună-ne la ${PHONE_DISPLAY} și stabilim totul la telefon.`,
    },
  ];

  return (
    <section className="bg-hydro-navy py-20 md:py-24">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[1.2fr_1fr]">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <h2 className="font-display text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl">
            Apă de puț în {name} — de ce merită.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-white/65">
            Tot mai multe gospodării din {name} aleg să nu mai depindă de rețeaua de apă:
            își fac propriul puț și au apă la robinet pentru casă, grădină și gospodărie,
            fără facturi care cresc de la o lună la alta. Un puț forat corect — cu țeavă
            PVC albastră cu filet, filtru și pietriș mărgăritar — îți dă apă limpede ani
            la rând, iar costul de întreținere e minim.
          </p>
          <p className="mt-4 text-base leading-relaxed text-white/65">
            Noi venim cu utilajul propriu în {name}, forăm până prindem pânza de apă,
            tubăm puțul segment cu segment și îl limpezim până apa iese curată. Prețul
            orientativ îl vezi mai jos, din calculator: selectezi adâncimea și localitatea{" "}
            {name}, iar deplasarea de {transport} e deja inclusă în estimare. Prețul ferm
            îl fixăm împreună, după vizita la teren — fără costuri-surpriză pe parcurs.
          </p>
          <p className="mt-4 text-base leading-relaxed text-white/65">
            Sună-ne la{" "}
            <a href={PHONE_TEL} data-testid="locality-content-call" className="font-bold text-hydro-cyan hover:text-white">
              {PHONE_DISPLAY}
            </a>{" "}
            sau trimite cererea din calculator și te sunăm noi pentru detalii și programare.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-7">
            <h3 className="font-display text-xl font-bold text-white">
              Ce include un foraj făcut de Puțul tău
            </h3>
            <ul className="mt-5 space-y-3.5">
              {INCLUDED.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-white/70">
                  <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-hydro-cyan/15 text-hydro-cyan">
                    <Check size={12} strokeWidth={3} />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-6 rounded-3xl border border-white/10 p-7">
            <h3 className="font-display text-lg font-bold text-white">Forăm și în apropiere:</h3>
            <div className="mt-4 flex flex-wrap gap-2">
              {around.map((l) => (
                <a
                  key={l.name}
                  href={`/foraje-puturi/${localitySlug(l.name)}`}
                  data-testid={`locality-nearby-${localitySlug(l.name)}`}
                  className="rounded-full border border-white/15 px-4 py-2 text-xs font-bold text-white/65 transition-colors hover:border-hydro-cyan hover:text-hydro-cyan"
                >
                  {l.name}
                </a>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      <div className="mx-auto mt-14 max-w-3xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <h3 className="font-display text-2xl font-extrabold text-white sm:text-3xl">
            Întrebări frecvente despre foraje în {name}
          </h3>
          <Accordion type="single" collapsible className="mt-6 space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem
                key={f.q}
                value={`item-${i}`}
                data-testid={`locality-faq-${i}`}
                className="rounded-2xl border border-white/10 bg-white/[0.04] px-6 last:border-b"
              >
                <AccordionTrigger className="py-5 text-left font-display text-base font-bold text-white hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="pb-6 text-sm leading-relaxed text-white/60">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}
